import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiSend, FiLock } from 'react-icons/fi';

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    chats = [], 
    currentUser, 
    friends = [], 
    sendMessage, 
    markChatAsRead 
  } = useAppContext();

  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);
  
  const chat = chats.find(c => c.id === id);
  const isFriend = chat ? friends.includes(chat.user?.id) : false;

  useEffect(() => {
    if (chat && chat.unread > 0 && isFriend) {
      markChatAsRead(id);
    }
    if (chat?.messages && isFriend) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [id, chat?.unread, chat?.messages, isFriend]);

  if (!chat || !isFriend) {
    return (
      <div className="max-w-3xl mx-auto h-[calc(100vh-64px)] sm:h-[80vh] sm:my-6 flex items-center justify-center bg-white dark:bg-dark-surface sm:rounded-2xl sm:shadow-sm border-x border-gray-100 dark:border-dark-border">
        <div className="text-center flex flex-col items-center gap-4 p-8">
          <div className="w-16 h-16 bg-gray-50 dark:bg-dark-card rounded-full flex items-center justify-center text-gray-400 dark:text-gray-600 mb-2">
            <SafeIcon icon={FiLock} className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-black text-brand-950 dark:text-white">Chat non disponibile</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted max-w-sm">
            {!chat ? "Questa conversazione non esiste." : "Devi essere amico per scambiare messaggi."}
          </p>
          <button onClick={() => navigate('/messages')} className="mt-4 px-6 py-3 bg-brand-600 text-white font-bold text-xs uppercase tracking-widest rounded-2xl shadow-lg hover:bg-brand-700 transition-all">
            Torna ai messaggi
          </button>
        </div>
      </div>
    );
  }

  const handleSend = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      sendMessage(id, inputText.trim());
      setInputText('');
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-dark-surface sm:my-6 sm:rounded-2xl sm:shadow-sm h-[calc(100vh-64px)] sm:h-[80vh] flex flex-col overflow-hidden border-x border-gray-100 dark:border-dark-border transition-colors">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-dark-border bg-white dark:bg-dark-surface flex items-center gap-4 shrink-0 shadow-sm z-10">
        <button onClick={() => navigate('/messages')} className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-card rounded-full transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate(`/profile/${chat.user?.id}`)}>
          <img src={chat.user?.avatar} alt="avatar" className="w-10 h-10 rounded-full object-cover border border-gray-100 dark:border-dark-border" />
          <div>
            <h2 className="font-bold text-gray-900 dark:text-white leading-tight group-hover:text-brand-600 transition-colors uppercase text-xs tracking-tight">{chat.user?.nickname}</h2>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${chat.user?.isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              <span className="text-[9px] text-gray-400 dark:text-dark-muted font-black uppercase tracking-wider">
                {chat.user?.isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50 dark:bg-dark-card/50 flex flex-col gap-4 custom-scrollbar">
        <div className="text-center text-[10px] text-gray-400 dark:text-dark-muted font-black uppercase tracking-[0.2em] my-4">Inizio conversazione</div>
        {chat.messages.map((msg, idx) => {
          const isMe = msg.sender === currentUser?.id;
          return (
            <div key={idx} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
              <div className={`max-w-[85%] px-4 py-3 rounded-2xl shadow-sm ${isMe ? 'bg-brand-600 text-white rounded-br-none' : 'bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-gray-800 dark:text-gray-200 rounded-bl-none'}`}>
                <p className="text-sm leading-relaxed font-medium">{msg.text}</p>
              </div>
              <span className="text-[9px] text-gray-400 dark:text-dark-muted mt-1.5 px-1 font-black uppercase tracking-tighter">{msg.time}</span>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-dark-border shrink-0 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.03)] dark:shadow-none">
        <form onSubmit={handleSend} className="flex items-center gap-3">
          <input 
            type="text" 
            value={inputText} 
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Scrivi un messaggio..." 
            className="flex-1 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border focus:bg-white dark:focus:bg-dark-surface focus:border-brand-500 focus:ring-4 focus:ring-brand-50/50 dark:focus:ring-brand-950/20 rounded-2xl px-5 py-3.5 outline-none transition-all text-sm font-bold shadow-inner dark:text-white"
          />
          <button 
            type="submit" 
            disabled={!inputText.trim()}
            className={`p-4 rounded-2xl flex items-center justify-center transition-all shadow-lg ${inputText.trim() ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-brand-100 dark:shadow-brand-900/20 scale-100 active:scale-95' : 'bg-gray-100 dark:bg-dark-card text-gray-400 dark:text-gray-700 shadow-none cursor-not-allowed'}`}
          >
            <SafeIcon icon={FiSend} className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
}